import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../lib/apiClient';
import { loadFromCache, saveToCache } from '../lib/ztaCache';

// Department-scoped ZT assessment data — capabilities, governance, signals,
// and PDP — with the cache-first fetch strategy and the four save paths.
export function useZtaDepartmentData(selectedDepartment) {
  const [capabilityData, setCapabilityData] = useState({});
  const [governanceData, setGovernanceData] = useState({});
  const [signalData, setSignalData] = useState({});
  const [pdpData, setPdpData] = useState({});
  // Per-save-kind pending flags — { capability, governance, signal, pdp }.
  // Never one shared boolean: each modal/save path reads only its own flag.
  const [saving, setSaving] = useState({});
  // Last failed save — { kind, message } — surfaced inline by the container.
  const [saveError, setSaveError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const clearSaveError = useCallback(() => setSaveError(null), []);
  const setSavingFor = (kind, value) =>
    setSaving(prev => ({ ...prev, [kind]: value }));

  // Helper to update cache when state changes
  const updateCache = useCallback((caps, gov, sig, pdp) => {
    if (selectedDepartment?.department_id) {
      saveToCache(selectedDepartment.department_id, {
        capabilities: caps,
        governance: gov,
        signals: sig,
        pdp: pdp
      });
    }
  }, [selectedDepartment?.department_id]);

  // Fetch department-specific data with cache-first strategy
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDepartment?.department_id) {
        setCapabilityData({});
        setGovernanceData({});
        setSignalData({});
        setPdpData({});
        setLoading(false);
        return;
      }

      const deptId = selectedDepartment.department_id;

      // Step 1: Load from cache immediately (instant display)
      const cached = loadFromCache(deptId);
      if (cached?.data) {
        setCapabilityData(cached.data.capabilities || {});
        setGovernanceData(cached.data.governance || {});
        setSignalData(cached.data.signals || {});
        setPdpData(cached.data.pdp || {});
        setLoading(false);

        // If cache is fresh, skip network fetch
        if (cached.isFresh) {
          return;
        }
        // Otherwise, refresh in background
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      // Step 2: Fetch fresh data from API
      try {
        const [capData, govData, sigData, pdpResult] = await Promise.all([
          apiClient.get(`/api/zero-trust/departments/${deptId}/vendor-mappings`).catch(() => ({ mappings: [] })),
          apiClient.get(`/api/zero-trust/departments/${deptId}/governance`).catch(() => ({ items: [] })),
          apiClient.get(`/api/zero-trust/departments/${deptId}/signals`).catch(() => ({ items: [] })),
          apiClient.get(`/api/zero-trust/departments/${deptId}/pdp`).catch(() => ({ data: {} }))
        ]);

        const mappedCaps = {};
        (capData.mappings || []).forEach(m => {
          mappedCaps[m.capability_id] = m;
        });

        const mappedGov = {};
        (govData.items || []).forEach(g => {
          mappedGov[g.governance_id] = g;
        });

        const mappedSig = {};
        (sigData.items || []).forEach(s => {
          mappedSig[s.signal_id] = s;
        });

        const pdp = pdpResult.data || {};

        // Update state
        setCapabilityData(mappedCaps);
        setGovernanceData(mappedGov);
        setSignalData(mappedSig);
        setPdpData(pdp);

        // Save to cache
        saveToCache(deptId, {
          capabilities: mappedCaps,
          governance: mappedGov,
          signals: mappedSig,
          pdp: pdp
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        // Only clear if we don't have cached data
        if (!cached?.data) {
          setCapabilityData({});
          setGovernanceData({});
          setSignalData({});
          setPdpData({});
        }
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    };
    fetchData();
  }, [selectedDepartment?.department_id]);

  // Save capability data — optimistic: patch local state first, persist
  // behind, revert on failure (TechMap-style local-state-first).
  const saveCapability = useCallback(async (capId, data) => {
    if (!selectedDepartment?.department_id) return;

    let previous;
    setCapabilityData(prev => {
      previous = prev[capId];
      return { ...prev, [capId]: { capability_id: capId, ...data } };
    });
    setSavingFor('capability', true);
    setSaveError(null);
    try {
      await apiClient.post(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/vendor-mappings`,
        { capability_id: capId, ...data }
      );
    } catch (err) {
      console.error('Error saving:', err);
      setCapabilityData(prev => {
        const next = { ...prev };
        if (previous === undefined) delete next[capId];
        else next[capId] = previous;
        return next;
      });
      setSaveError({ kind: 'capability', message: 'Capability changes could not be saved and were reverted.' });
    } finally {
      setSavingFor('capability', false);
    }
  }, [selectedDepartment?.department_id]);

  // Save governance data — optimistic, reverts on failure
  const saveGovernance = useCallback(async (govId, data) => {
    if (!selectedDepartment?.department_id) return;

    let previous;
    setGovernanceData(prev => {
      previous = prev[govId];
      return { ...prev, [govId]: { governance_id: govId, ...data } };
    });
    setSavingFor('governance', true);
    setSaveError(null);
    try {
      await apiClient.post(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/governance`,
        { governance_id: govId, ...data }
      );
    } catch (err) {
      console.error('Error saving governance:', err);
      setGovernanceData(prev => {
        const next = { ...prev };
        if (previous === undefined) delete next[govId];
        else next[govId] = previous;
        return next;
      });
      setSaveError({ kind: 'governance', message: 'Governance changes could not be saved and were reverted.' });
    } finally {
      setSavingFor('governance', false);
    }
  }, [selectedDepartment?.department_id]);

  // Save signal data — optimistic, reverts on failure
  const saveSignal = useCallback(async (signalId, data) => {
    if (!selectedDepartment?.department_id) return;

    let previous;
    setSignalData(prev => {
      previous = prev[signalId];
      return { ...prev, [signalId]: { signal_id: signalId, ...data } };
    });
    setSavingFor('signal', true);
    setSaveError(null);
    try {
      await apiClient.post(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/signals`,
        { signal_id: signalId, ...data }
      );
    } catch (err) {
      console.error('Error saving signal:', err);
      setSignalData(prev => {
        const next = { ...prev };
        if (previous === undefined) delete next[signalId];
        else next[signalId] = previous;
        return next;
      });
      setSaveError({ kind: 'signal', message: 'Signal changes could not be saved and were reverted.' });
    } finally {
      setSavingFor('signal', false);
    }
  }, [selectedDepartment?.department_id]);

  // Save PDP data — optimistic, reverts on failure
  const savePDP = useCallback(async (data) => {
    if (!selectedDepartment?.department_id) return;

    let previous;
    setPdpData(prev => {
      previous = prev;
      return data;
    });
    setSavingFor('pdp', true);
    setSaveError(null);
    try {
      await apiClient.post(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/pdp`,
        data
      );
    } catch (err) {
      console.error('Error saving PDP:', err);
      setPdpData(previous);
      setSaveError({ kind: 'pdp', message: 'Policy Decision Point changes could not be saved and were reverted.' });
    } finally {
      setSavingFor('pdp', false);
    }
  }, [selectedDepartment?.department_id]);

  // Sync state to cache whenever data changes (after saves)
  useEffect(() => {
    if (selectedDepartment?.department_id && !loading) {
      updateCache(capabilityData, governanceData, signalData, pdpData);
    }
  }, [capabilityData, governanceData, signalData, pdpData, selectedDepartment?.department_id, loading, updateCache]);

  return {
    capabilityData,
    governanceData,
    signalData,
    pdpData,
    setSignalData,
    setPdpData,
    saving,
    saveError,
    clearSaveError,
    isRefreshing,
    saveCapability,
    saveGovernance,
    saveSignal,
    savePDP,
  };
}
